import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Box, Button, Chip, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { Add, ArrowBack, Autorenew, Check, Close, Edit, FileUpload, Settings } from "@mui/icons-material";

import { useQueryClient } from "@tanstack/react-query";
import { useInvoiceDetail, useInvoiceAttachments, useV1InvoiceDetail, usePODetails } from "../api/invoiceQueries";
import { paymentsForInvoice, latestInvoicePayment, isAdvancePending } from "@/pages/InvoiceManagement/utils/payments";
import { useInvoiceAction, useRequestRevision } from "../api/invoiceMutations";
import { useCreatePaymentRequest } from "@/pages/Payments/api/paymentMutations";
import InvoiceReview from "@/pages/InvoiceManagement/InvoiceReview";
import { useToast } from "@/pages/InvoiceManagement/hooks/useToast";
import LoaderV2 from "@/pages/InvoiceManagement/components/LoaderV2";
import { usePermissions } from "@/context/PermissionsContext";
import { BOUNCER_PERMISSIONS } from "@/constants/bouncerPermissions";
import { useIsMobile } from "@/hooks/useIsMobile";
import ActionMenu from "@/components/mobile/ActionMenu";
import StickyActionBar from "@/components/mobile/StickyActionBar";

import InvoiceDetailsCard from "../components/InvoiceDetailsCard";
import LineItemsCard from "../components/LineItemsCard";
import PoDetailsCard from "../components/PoDetailsCard";
// import GrnStatusCard from "../components/GrnStatusCard"; // ENG-22651: GRN details moved to PoDetailsCard (Order Details tab); card hidden here pending UX decision on whether to re-enable
import PaymentSummaryCard from "../components/PaymentSummaryCard";
import ThreeWayMatchCard from "../components/ThreeWayMatchCard";
import StatusBadge from "../components/StatusBadge";
import SystemFailureAlert from "../components/SystemFailureAlert";
import RejectModal from "../modals/RejectModal";
import RevisionModal from "../modals/RevisionModal";
import InvoiceReviewModal from "../modals/InvoiceReviewModal";
import GrnLinkModal from "../modals/GrnLinkModal";
import UploadInvoicesModal from "../modals/UploadInvoicesModal";
import ReprocessInvoiceModal from "../modals/ReprocessInvoiceModal";
import CreditNoteReview from "@/pages/InvoiceManagement/CreditNoteReview";
import { useInvoiceUploadFlow } from "../hooks/useInvoiceUploadFlow";
import { useBatchManager } from "../hooks/useBatchManager";
import { useReprocessInvoice, reprocessErrorMessage, type ReprocessOutcome } from "../hooks/useReprocessInvoice";
import { invoiceKeys } from "../api/queryKeys";

import { LIFECYCLE_STATUS, getEffectiveStatus } from "../types/common";
import { getStatusLabel, isFinsSource } from "../constants/lifecycleStatus";
import { showsExceedsLimitChip } from "../utils/exceedsLimit";
import { ACTION, type Action } from "../types/action";
import { V2_COLORS } from "../constants/colors";
import { deriveThreeWayMatchAmounts } from "../utils/threeWayMatch";
import { systemFailureNotice } from "../utils/systemFailure";

/**
 * Header action button config.
 * Maps allowed_actions to buttons displayed in the header top-right.
 * The order matters — buttons appear left-to-right in this order.
 */
const HEADER_ACTION_CONFIG: Record<
  string,
  { label: string; icon: React.ElementType; variant: "contained" | "outlined"; sx: Record<string, unknown> }
> = {
  [ACTION.REQUEST_REVISION]: {
    label: "Revise",
    icon: Edit,
    variant: "outlined",
    sx: { color: "#333", borderColor: "#CCC", "&:hover": { borderColor: "#999", backgroundColor: "#F5F5F5" } },
  },
  [ACTION.REJECT]: {
    label: "Reject",
    icon: Close,
    variant: "contained",
    sx: { color: "#333", backgroundColor: "#E6E6E6", "&:hover": { backgroundColor: "#D6D6D6" }, boxShadow: "none" },
  },
  [ACTION.OVERRIDE_EXCEPTION]: {
    label: "Override",
    icon: Settings,
    variant: "outlined",
    sx: { color: V2_COLORS.warning, borderColor: V2_COLORS.warning, "&:hover": { borderColor: V2_COLORS.warning, backgroundColor: V2_COLORS.warningBg } },
  },
  [ACTION.RESOLVE_EXCEPTION]: {
    label: "Resolve",
    icon: Check,
    variant: "contained",
    sx: { backgroundColor: V2_COLORS.success, "&:hover": { backgroundColor: V2_COLORS.success }, boxShadow: "none" },
  },
  [ACTION.SELECT_GRN]: {
    label: "Link GRN",
    icon: Settings,
    variant: "contained",
    sx: { backgroundColor: V2_COLORS.primary, "&:hover": { backgroundColor: V2_COLORS.primary }, boxShadow: "none" },
  },
  [ACTION.APPROVE]: {
    label: "Approve",
    icon: Check,
    variant: "contained",
    sx: { backgroundColor: V2_COLORS.primary, color: "#FFFFFF", "&:hover": { backgroundColor: V2_COLORS.primary }, boxShadow: "none" },
  },
};

/** Actions that are shown in the header. */
const HEADER_ACTIONS: Set<Action> = new Set([
  ACTION.APPROVE,
  ACTION.REJECT,
  ACTION.REQUEST_REVISION,
  ACTION.OVERRIDE_EXCEPTION,
  ACTION.RESOLVE_EXCEPTION,
  ACTION.SELECT_GRN,
]);

export default function InvoiceDetail() {
  const { invoiceId: invoiceIdParam } = useParams<{ invoiceId: string }>();
  const invoiceId = Number(invoiceIdParam);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();

  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const isMobile = useIsMobile();
  const canEdit = hasPermission(BOUNCER_PERMISSIONS.INVOICE_EDIT);
  const canUpload = hasPermission(BOUNCER_PERMISSIONS.INVOICE_UPLOAD);
  const isInternal = hasPermission(BOUNCER_PERMISSIONS.ACCOUNTS_PAYABLE_VIEW);
  const canCreatePaymentRequest = hasPermission(BOUNCER_PERMISSIONS.PAYMENT_EDIT);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(searchParams.get("review") === "true");
  const [v1ReviewOpen, setV1ReviewOpen] = useState(false);
  const [grnLinkOpen, setGrnLinkOpen] = useState(false);
  const [reprocessOpen, setReprocessOpen] = useState(false);
  const [cnId, setCnId] = useState<number | null>(() => {
    const raw = searchParams.get("cn");
    return raw ? Number(raw) : null;
  });

  const { data, isLoading, isError } = useInvoiceDetail(invoiceId);
  const { data: attachmentsData } = useInvoiceAttachments(invoiceId);
  const { data: poData, isLoading: isPoLoading } = usePODetails(data?.invoice?.po_code ?? "");
  const actionMutation = useInvoiceAction(invoiceId);
  const revisionMutation = useRequestRevision(invoiceId);
  const createPaymentMutation = useCreatePaymentRequest();

  // v1 invoice detail — fetched only when review is triggered on a v1 invoice
  const isV1Invoice = !!data && !data.invoice?.lifecycle_status;
  const {
    data: v1InvoiceDetail,
    isLoading: isV1DetailLoading,
    isError: isV1DetailError,
  } = useV1InvoiceDetail(v1ReviewOpen ? invoiceId : null);

  useEffect(() => {
    if (isV1DetailError && v1ReviewOpen) {
      showToast({ type: "error", message: "Failed to load invoice details" });
      setV1ReviewOpen(false);
    }
  }, [isV1DetailError, v1ReviewOpen, showToast]);

  // If review=true in URL and this is a v1 invoice, trigger v1 review on load
  useEffect(() => {
    if (data && isV1Invoice && searchParams.get("review") === "true") {
      setReviewOpen(false);
      setV1ReviewOpen(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // searchParams intentionally excluded: this effect only needs to run when `data` first loads
  // to handle the ?review=true deep-link. Re-running on searchParams changes would incorrectly
  // re-trigger the v1 review modal whenever the URL is updated for unrelated reasons.
  }, [data, isV1Invoice]);

  const isActionLoading = actionMutation.isLoading || revisionMutation.isLoading || createPaymentMutation.isLoading;

  const handleAction = useCallback(
    (action: Action) => {
      if (action === ACTION.REJECT) {
        setRejectOpen(true);
        return;
      }
      if (action === ACTION.REQUEST_REVISION) {
        setRevisionOpen(true);
        return;
      }
      if (action === ACTION.SELECT_GRN) {
        setGrnLinkOpen(true);
        return;
      }

      if (!data) return;
      actionMutation.mutate(
        { action, lifecycle_version: data.invoice.lifecycle_version, current_status: data.invoice.lifecycle_status },
        {
          onSuccess: (res) => {
            showToast({ type: "success", message: res.message || `${action} successful` });
          },
          onError: (err: unknown) => {
            const message = err instanceof Error ? err.message : "Action failed";
            showToast({ type: "error", message });
          },
        }
      );
    },
    [data, actionMutation, showToast]
  );

  const handleReject = useCallback(
    (reason: string) => {
      if (!data) return;
      actionMutation.mutate(
        { action: ACTION.REJECT, lifecycle_version: data.invoice.lifecycle_version, current_status: data.invoice.lifecycle_status, comments: reason },
        {
          onSuccess: () => {
            setRejectOpen(false);
            showToast({ type: "success", message: "Invoice rejected" });
          },
          onError: (err: unknown) => {
            const message = err instanceof Error ? err.message : "Rejection failed";
            showToast({ type: "error", message });
          },
        }
      );
    },
    [data, actionMutation, showToast]
  );

  const handleRevision = useCallback(
    (reason: string) => {
      revisionMutation.mutate(
        { revision_reason: reason },
        {
          onSuccess: () => {
            setRevisionOpen(false);
            showToast({ type: "success", message: "Revision request sent" });
          },
          onError: (err: unknown) => {
            const message = err instanceof Error ? err.message : "Revision request failed";
            showToast({ type: "error", message });
          },
        }
      );
    },
    [revisionMutation, showToast]
  );

  const handleCreatePaymentRequest = useCallback(() => {
    createPaymentMutation.mutate(invoiceId, {
      onSuccess: (res) => {
        showToast({
          type: res.created ? "success" : "info",
          message:
            res.message ??
            (res.created ? "Payment request created successfully" : "No payment request was created"),
        });
        // Payments now ride on PO details (ENG-27224), so refresh that instead.
        queryClient.invalidateQueries(invoiceKeys.poDetails(data?.invoice?.po_code ?? ""));
      },
      onError: (err: unknown) => {
        const message = err instanceof Error ? err.message : "Failed to create payment request";
        showToast({ type: "error", message });
      },
    });
  }, [createPaymentMutation, invoiceId, showToast, queryClient, data?.invoice?.po_code]);

  const batchManager = useBatchManager({
    onBatchComplete: useCallback(() => {
      queryClient.invalidateQueries(invoiceKeys.detail(invoiceId));
      queryClient.invalidateQueries(invoiceKeys.lists());
    }, [queryClient, invoiceId]),
  });

  const flow = useInvoiceUploadFlow({
    onBatchStarted: batchManager.addBatch,
    poCode: data?.invoice?.po_code,
    invoiceId,
  });

  const invoice = data?.invoice;
  const primaryAttachment = attachmentsData?.attachments?.[0] ?? null;

  const reprocess = useReprocessInvoice(invoiceId, {
    onFinished: (outcome: ReprocessOutcome) => {
      if (outcome.kind === "success") {
        showToast({ type: "success", message: "Invoice reprocessed" });
      } else if (outcome.kind === "validation_failed") {
        showToast({ type: "error", message: `Invoice reprocessed, but validation failed${outcome.reason ? `: ${outcome.reason}` : ""}` });
      } else if (outcome.kind === "unchanged") {
        showToast({ type: "error", message: `Reprocess didn't change the invoice${outcome.reason ? `: ${outcome.reason}` : ""}` });
      } else if (outcome.kind === "status_error") {
        showToast({ type: "warning", message: "Couldn't get the reprocess status. Refreshed the invoice; check again in a few minutes." });
      } else {
        showToast({ type: "info", message: "Reprocess finished — refreshed the invoice" });
      }
    },
  });
  // The backend decides who may reprocess and when (allowed_actions); we reprocess the document shown.
  const showReprocess = !!data?.allowed_actions?.includes(ACTION.REPROCESS) && !!primaryAttachment;
  // Reprocess and the other actions lock each other out; Upload also waits for a reprocess,
  // since a new attachment would change the document being re-read.
  const actionsLocked = isActionLoading || reprocess.isRunning;

  const handleConfirmReprocess = useCallback(() => {
    if (!primaryAttachment) return;
    reprocess.start(primaryAttachment.id, {
      onSuccess: () => {
        setReprocessOpen(false);
        showToast({ type: "info", message: "Reprocess started. This can take a few minutes." });
      },
      onError: (err: unknown) => showToast({ type: "error", message: reprocessErrorMessage(err) }),
    });
  }, [primaryAttachment, reprocess, showToast]);
  // const grn_mappings = data?.grn_mappings;
  const poId = poData?.po_list?.[0]?.metadata?.po_id ?? null;
  const poInvoiceRow = poData?.po_list?.[0]?.invoices?.find(
    (inv) => inv.invoice_no === data?.invoice?.invoice_no,
  );
  // Payment lines for this invoice come from PO details `payments[]` (legacy +
  // v2 union, ENG-27224). Matched on invoice_no only — v2 lines are typed
  // FINAL/RUNNING, not the legacy "Invoice".
  const invoicePayments = paymentsForInvoice(poData?.po_list?.[0]?.payments, data?.invoice?.invoice_no);
  const poPaymentEntry = latestInvoicePayment(poData?.po_list?.[0]?.payments, data?.invoice?.invoice_no);
  const { poAmount, totalInvoiceAmount, grnAmount } = deriveThreeWayMatchAmounts(poData);
  const line_items = data?.line_items;
  const allowed_actions = data?.allowed_actions;
  const approval_logs = data?.approval_logs;

  // Detail API returns invoice_status as a sibling of invoice (data.invoice_status),
  // not nested inside data.invoice. Merge it so the fallback chain works.
  const effectiveStatus = invoice
    ? getEffectiveStatus({ ...invoice, invoice_status: data.invoice_status })
    : undefined;

  // Derive rejection reason from latest REJECT approval log (not a direct API field)
  const rejectionReason = useMemo(() => {
    if (!invoice || effectiveStatus !== LIFECYCLE_STATUS.REJECTED) return undefined;
    const rejectLog = approval_logs
      ?.filter((log) => log.approval_action === "REJECT")
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
    return rejectLog?.comments;
  }, [invoice, effectiveStatus, approval_logs]);

  const systemFailure = useMemo(
    () => systemFailureNotice(effectiveStatus, invoice?.system_rejection_reason, isInternal),
    [effectiveStatus, invoice?.system_rejection_reason, isInternal]
  );

  const { isRejected, showDeleteInHeader, showEdit, headerActions, showExceedsLimit, showThreeWayMatch, showCreatePaymentRequest } =
    useMemo(() => {
      if (!invoice || !effectiveStatus) {
        return { isRejected: false, showDeleteInHeader: false, showEdit: false, headerActions: [] as Action[], showExceedsLimit: false, showThreeWayMatch: false, showCreatePaymentRequest: false };
      }
      const allowed = allowed_actions ?? [];
      const rejected = effectiveStatus === LIFECYCLE_STATUS.REJECTED;
      const hasPendingRevision = (data?.revision_requests ?? []).some(
        (r: { status: string }) => r.status === "PENDING"
      );
      const actions = allowed
        .filter((a) => HEADER_ACTIONS.has(a))
        .filter((a) => a !== ACTION.REQUEST_REVISION || !hasPendingRevision);

      return {
        isRejected: rejected,
        showDeleteInHeader:
          effectiveStatus === LIFECYCLE_STATUS.UPLOADED &&
          allowed.includes(ACTION.REJECT),
        showEdit: canEdit && allowed.includes(ACTION.EDIT),
        headerActions: actions,
        // See showsExceedsLimitChip: suppressed for FINS, where the chip would
        // be categorically false and contradict the "OPS Approval Pending" badge.
        showExceedsLimit: showsExceedsLimitChip({
          effectiveStatus,
          poSource: data?.po_source,
          headerActions: actions,
        }),
        showThreeWayMatch: !rejected && !!data.match_result,
        showCreatePaymentRequest:
          effectiveStatus === LIFECYCLE_STATUS.APPROVED && canCreatePaymentRequest && isInternal,
      };
    }, [invoice, effectiveStatus, allowed_actions, canEdit, canCreatePaymentRequest, isInternal, data?.po_source, data?.revision_requests, data?.match_result]);

  // Mobile header kebab holds the secondary actions; primary actions
  // (approve / reject / revise / …) live in the sticky bottom bar instead.
  const secondaryActions = useMemo(
    () => [
      ...(canUpload
        ? [{ label: "Upload", icon: <FileUpload fontSize="small" />, onClick: () => setUploadOpen(true), disabled: reprocess.isRunning }]
        : []),
      ...(showEdit
        ? [{
            label: "Edit",
            icon: <Edit fontSize="small" />,
            onClick: () => navigate(`/dashboard/invoice-management/v2/${invoiceId}/edit`),
            disabled: reprocess.isRunning,
          }]
        : []),
      ...(showReprocess
        ? [{
            label: "Reprocess invoice",
            icon: <Autorenew fontSize="small" />,
            onClick: () => setReprocessOpen(true),
            disabled: actionsLocked,
          }]
        : []),
      ...(showDeleteInHeader
        ? [{
            label: "Delete invoice",
            icon: <Close fontSize="small" />,
            onClick: () => handleAction(ACTION.REJECT),
            destructive: true,
            disabled: actionsLocked,
          }]
        : []),
    ],
    [canUpload, showEdit, showReprocess, reprocess.isRunning, showDeleteInHeader, actionsLocked, navigate, invoiceId, handleAction],
  );

  /** One header-action button; shared by the desktop header, the review modal and the mobile sticky bar. */
  const renderActionButton = (action: Action, tall = false) => {
    const config = HEADER_ACTION_CONFIG[action];
    if (!config) return null;
    const ActionIcon = config.icon;
    return (
      <Button
        key={action}
        variant={config.variant}
        size="small"
        disabled={actionsLocked}
        startIcon={isActionLoading ? <CircularProgress size={14} /> : <ActionIcon sx={{ fontSize: 16 }} />}
        onClick={() => handleAction(action)}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: "13px",
          height: tall ? 40 : 32,
          borderRadius: "4px",
          ...config.sx,
        }}
      >
        {config.label}
      </Button>
    );
  };

  if (isLoading) return <LoaderV2 />;

  if (isError || !data || !invoice) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="60vh">
        <Typography variant="h6" color="error">
          Error loading invoice details
        </Typography>
      </Box>
    );
  }

  // Cards are shared by the desktop two-column layout and the mobile single column.
  const invoiceDetailsCard = (
    <InvoiceDetailsCard
      detail={data}
      onReview={() => isV1Invoice ? setV1ReviewOpen(true) : setReviewOpen(true)}
      showExtras={isMobile}
      onCreditNote={(creditNote) => setCnId(creditNote.id)}
      amountPaid={poInvoiceRow?.amount_paid}
      paymentDate={poPaymentEntry?.payment_date}
      poDetails={poData?.po_list?.[0] ? {
        source: poData.po_list[0].source,
        milestone: poData.po_list[0].milestone,
        invoices: poData.po_list[0].invoices,
      } : undefined}
    />
  );
  const lineItemsCard = line_items && line_items.length > 0 && (
    <LineItemsCard lineItems={line_items} isMobile={isMobile} />
  );
  const poDetailsCard = <PoDetailsCard poCode={invoice.po_code} />;
  const paymentSummaryCard = (
    <PaymentSummaryCard
      payments={invoicePayments}
      cashPaid={poInvoiceRow?.amount_paid}
      advanceAdjusted={poInvoiceRow?.advance_adjusted}
      openAdvanceAdjusted={poInvoiceRow?.open_advance_adjusted}
      invoiceValue={poInvoiceRow?.invoice_value}
      advancePending={isAdvancePending(poData?.po_list?.[0])}
      isLoading={isPoLoading}
      isMobile={isMobile}
    />
  );
  // ENG-22651: GrnStatusCard hidden — GRN details surfaced on PoDetailsCard (Order Details tab).
  // May be re-enabled here (right column / after paymentSummaryCard) if the UX requirement changes:
  //   <GrnStatusCard grnMappings={grn_mappings ?? []} grnId={invoice.grn_id} grnDate={invoice.grn_date}
  //     grnAmount={data.grn_amount} grnAllocationStatus={invoice.grn_allocation_status}
  //     onLinkGrn={allowed_actions?.includes(ACTION.SELECT_GRN) ? () => setGrnLinkOpen(true) : undefined} />
  const threeWayMatchCard = showThreeWayMatch && (
    <ThreeWayMatchCard
      matchResult={data.match_result ?? null}
      poAmount={poAmount}
      invoiceAmount={totalInvoiceAmount}
      grnAmount={grnAmount}
      isFins={isFinsSource(data?.po_source ?? poData?.po_list?.[0]?.source)}
      isMobile={isMobile}
    />
  );
  const showStickyBar = isMobile && (headerActions.length > 0 || showCreatePaymentRequest);

  return (
    // Mobile: the page is its own scroll region (header + sticky bar fixed, cards scroll).
    // The Dashboard workspace has overflow:hidden, so a plain position:sticky bar would
    // only pin to the bottom of the full content height, i.e. appear after scrolling to the end.
    <Box
      sx={
        isMobile
          ? { display: "flex", flexDirection: "column", height: "calc(100vh - 48px)" }
          : { px: 3, pb: 3 }
      }
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={isMobile ? 2 : 3}
        sx={{
          bgcolor: "background.paper",
          px: isMobile ? 1.5 : 2.5,
          py: 1,
          mx: isMobile ? 0 : -3,
          flexShrink: 0,
          boxShadow: "0px 0px 2px 0px rgba(0,0,0,0.2)",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={() => navigate(-1)} size="small">
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="body2" color="textSecondary" display="block">
              Invoice id/
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography fontSize="16px" fontWeight={600} lineHeight="24px">
                {invoice.invoice_no || "Invoice details"}
              </Typography>
              <StatusBadge
                status={effectiveStatus!}
                displayLabel={!invoice.lifecycle_status ? data.invoice_status?.label : getStatusLabel(effectiveStatus!, isInternal, data.po_source)}
              />
            </Box>
          </Box>
        </Box>

        {/* Header actions: mobile → status chip + kebab; desktop → full button row */}
        {isMobile ? (
          <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
            {showExceedsLimit && (
              <Chip
                label="EXCEEDS LIMIT"
                size="small"
                sx={{ fontWeight: 600, color: V2_COLORS.warning, backgroundColor: V2_COLORS.warningBg }}
              />
            )}
            <ActionMenu actions={secondaryActions} />
          </Box>
        ) : (
        <Box display="flex" gap={1.5} alignItems="center">
          {showDeleteInHeader && (
            <Button
              variant="contained"
              size="small"
              disabled={actionsLocked}
              startIcon={isActionLoading ? <CircularProgress size={14} /> : undefined}
              onClick={() => handleAction(ACTION.REJECT)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                backgroundColor: V2_COLORS.error,
                "&:hover": { backgroundColor: V2_COLORS.errorHover },
              }}
            >
              Delete invoice
            </Button>
          )}
          {canUpload && (
            <Button
              variant="outlined"
              size="small"
              disabled={reprocess.isRunning}
              startIcon={<FileUpload fontSize="small" />}
              onClick={() => setUploadOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                height: 32,
                borderRadius: "4px",
                color: "#333",
                borderColor: "#CCC",
                "&:hover": { borderColor: "#999", backgroundColor: "#F5F5F5" },
              }}
            >
              Upload
            </Button>
          )}
          {showEdit && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Edit fontSize="small" />}
              disabled={reprocess.isRunning}
              onClick={() => navigate(`/dashboard/invoice-management/v2/${invoiceId}/edit`)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                height: 32,
                borderRadius: "4px",
                color: "#333",
                borderColor: "#CCC",
                "&:hover": { borderColor: "#999", backgroundColor: "#F5F5F5" },
              }}
            >
              Edit
            </Button>
          )}
          {showReprocess && (
            <Tooltip title="Re-read the invoice document and run validation and matching again">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={actionsLocked}
                  startIcon={reprocess.isRunning ? <CircularProgress size={14} /> : <Autorenew fontSize="small" />}
                  onClick={() => setReprocessOpen(true)}
                  sx={{
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "13px",
                    height: 32,
                    borderRadius: "4px",
                    color: "#333",
                    borderColor: "#CCC",
                    "&:hover": { borderColor: "#999", backgroundColor: "#F5F5F5" },
                  }}
                >
                  {reprocess.isRunning ? "Reprocessing…" : "Reprocess invoice"}
                </Button>
              </span>
            </Tooltip>
          )}
          {showExceedsLimit && (
            <Chip
              label="EXCEEDS LIMIT"
              size="small"
              sx={{ fontWeight: 600, color: V2_COLORS.warning, backgroundColor: V2_COLORS.warningBg }}
            />
          )}
          {headerActions.map((action) => renderActionButton(action))}
          {showCreatePaymentRequest && (
            <Button
              variant="contained"
              size="small"
              disabled={actionsLocked}
              startIcon={isActionLoading ? <CircularProgress size={14} /> : <Add sx={{ fontSize: 16 }} />}
              onClick={handleCreatePaymentRequest}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                height: 32,
                borderRadius: "4px",
                backgroundColor: V2_COLORS.primary,
                color: "#FFFFFF",
                "&:hover": { backgroundColor: V2_COLORS.primary },
                boxShadow: "none",
              }}
            >
              Create Payment Request
            </Button>
          )}
        </Box>
        )}
      </Box>

      <Box
        sx={
          isMobile
            ? { px: 2, pb: 2, flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch" }
            : undefined
        }
      >
        {/* Rejection banner */}
        {isRejected && rejectionReason && (
          <Alert severity="error" sx={{ mb: 2.5 }}>
            Rejected: {rejectionReason}. Please edit and resubmit.
          </Alert>
        )}

        {reprocess.isRunning && (
          <Alert severity="info" sx={{ mb: 2.5 }}>
            Reprocessing this invoice. It will refresh when done; you can leave this page meanwhile.
          </Alert>
        )}

        {/* System failure banner (OCR_FAILED / D365_FAILED) */}
        <SystemFailureAlert notice={systemFailure} sx={{ mb: 2.5 }} />

        {/* Desktop: two columns (invoice details + PO/GRN). Mobile: one column in reading order. */}
        {isMobile ? (
          <Box display="flex" flexDirection="column" gap={2}>
            {invoiceDetailsCard}
            {poDetailsCard}
            {lineItemsCard}
            {paymentSummaryCard}
            {threeWayMatchCard}
          </Box>
        ) : (
          <Box display="flex" gap={3} alignItems="flex-start">
            {/* Left column */}
            <Box flex={1} display="flex" flexDirection="column" gap={2.5}>
              {invoiceDetailsCard}
              {lineItemsCard}
            </Box>

            {/* Right column */}
            <Box flex={1} display="flex" flexDirection="column" gap={2.5}>
              {poDetailsCard}
              {paymentSummaryCard}
              {threeWayMatchCard}
            </Box>
          </Box>
        )}
      </Box>

      {/* Mobile: primary actions pinned to the bottom of the viewport */}
      {showStickyBar && (
        <StickyActionBar>
          {headerActions.map((action) => renderActionButton(action, true))}
          {showCreatePaymentRequest && (
            <Button
              variant="contained"
              size="small"
              disabled={actionsLocked}
              startIcon={isActionLoading ? <CircularProgress size={14} /> : <Add sx={{ fontSize: 16 }} />}
              onClick={handleCreatePaymentRequest}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                height: 40,
                borderRadius: "4px",
                backgroundColor: V2_COLORS.primary,
                color: "#FFFFFF",
                "&:hover": { backgroundColor: V2_COLORS.primary },
                boxShadow: "none",
              }}
            >
              Create Payment Request
            </Button>
          )}
        </StickyActionBar>
      )}

      {/* Modals */}
      {!isV1Invoice && (
        <InvoiceReviewModal
          open={reviewOpen}
          onClose={() => {
            setReviewOpen(false);
            setSearchParams((prev) => { prev.delete("review"); return prev; }, { replace: true });
          }}
          invoiceNo={invoice.invoice_no}
          invoiceImage={primaryAttachment ? { file_name: primaryAttachment.name, pre_signed_url: primaryAttachment.url } : null}
          checks={data.validation_result?.response?.checks ?? []}
          lifecycleStatus={effectiveStatus}
          parseResponse={data.parse_result ?? null}
          systemFailure={systemFailure}
          actions={
            (showEdit || headerActions.length > 0)
              ? [
                  showEdit && (
                    <Button
                      key="edit"
                      variant="outlined"
                      size="small"
                      disabled={reprocess.isRunning}
                      startIcon={<Edit fontSize="small" />}
                      onClick={() => navigate(`/dashboard/invoice-management/v2/${invoiceId}/edit`)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "13px",
                        height: isMobile ? 40 : 32,
                        borderRadius: "4px",
                        color: "#333",
                        borderColor: "#CCC",
                        "&:hover": { borderColor: "#999", backgroundColor: "#F5F5F5" },
                      }}
                    >
                      Edit
                    </Button>
                  ),
                  ...headerActions.map((action) => renderActionButton(action, isMobile)),
                ].filter(Boolean)
              : undefined
          }
        />
      )}
      {v1ReviewOpen && isV1DetailLoading && (
        <Box sx={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          bgcolor: "rgba(0,0,0,0.3)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 1300,
        }}>
          <CircularProgress />
        </Box>
      )}
      {v1ReviewOpen && v1InvoiceDetail && (
        <InvoiceReview
          invoice={v1InvoiceDetail}
          handleClose={() => {
            setV1ReviewOpen(false);
            setSearchParams((prev) => { prev.delete("review"); return prev; }, { replace: true });
          }}
        />
      )}
      <RejectModal
        open={rejectOpen}
        loading={actionMutation.isLoading}
        onClose={() => setRejectOpen(false)}
        onConfirm={handleReject}
      />
      <RevisionModal
        open={revisionOpen}
        loading={revisionMutation.isLoading}
        onClose={() => setRevisionOpen(false)}
        onConfirm={handleRevision}
      />
      <ReprocessInvoiceModal
        open={reprocessOpen}
        loading={reprocess.isRunning}
        invoiceNo={invoice.invoice_no}
        isApproved={invoice.lifecycle_status === LIFECYCLE_STATUS.APPROVED}
        onClose={() => setReprocessOpen(false)}
        onConfirm={handleConfirmReprocess}
      />
      <GrnLinkModal
        open={grnLinkOpen}
        onClose={() => setGrnLinkOpen(false)}
        invoiceId={invoiceId}
        poCode={invoice.po_code}
        lifecycleVersion={invoice.lifecycle_version}
      />
      {cnId && (
        <CreditNoteReview
          creditNote={{ id: cnId }}
          po_code={invoice.po_code}
          po_id={poId}
          disableRedirect
          handleClose={() => {
            setCnId(null);
            setSearchParams((prev) => { prev.delete("cn"); return prev; }, { replace: true });
          }}
        />
      )}
      <UploadInvoicesModal
        open={uploadOpen}
        onClose={() => { setUploadOpen(false); flow.reset(); }}
        phase={flow.phase}
        items={flow.items}
        summary={flow.summary}
        rejectedFiles={flow.rejectedFiles}
        batchStartError={flow.batchStartError}
        maxFiles={flow.maxFiles}
        activeBatches={batchManager.batches}
        onDismissBatch={batchManager.dismissBatch}
        onFilesSelected={flow.addFiles}
        onUploadAll={flow.startUpload}
        onRemoveFile={flow.cancelFile}
        onRetryFile={flow.retryFile}
        onRetryAllFailed={flow.retryAllFailed}
        onRetryBatchProcessing={flow.retryBatchProcessing}
        onDismissRejected={flow.dismissRejected}
      />
    </Box>
  );
}
